import { useQueries } from '@tanstack/react-query';
import { ordersService, OrderLineItemRow } from '@/services/ordersService';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Fetches line items for multiple orders in parallel.
 * Used for eager loading line items for all visible orders.
 */
export const useOrdersLineItems = (orderIds: string[]) => {
  const queries = useQueries({
    queries: orderIds.map((orderId) => ({
      queryKey: queryKeys.orders.lineItems(orderId),
      queryFn: () => ordersService.getOrderLineItems(orderId),
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000, // Keep data in cache for 5 minutes
      enabled: Boolean(orderId),
      placeholderData: (previousData: OrderLineItemRow[] | undefined) => previousData, // Keep previous data while refetching
    })),
  });

  // Build a map of orderId -> line items
  const lineItemsByOrderId = new Map<string, OrderLineItemRow[]>();
  queries.forEach((query, index) => {
    const orderId = orderIds[index];
    // Include data even if stale, as long as it exists
    if (orderId && query.data) {
      lineItemsByOrderId.set(orderId, query.data);
    }
  });

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  return {
    lineItemsByOrderId,
    isLoading,
    isError,
    queries,
  };
};
